import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaExclamationTriangle, FaExternalLinkAlt, FaRedo } from "react-icons/fa";

/**
 * Presentational shell for every Org 360 section. Owns the chrome (title,
 * accent rail, status, footer deep-link) so the section components only
 * concern themselves with fetching + rendering their own body. Loading and
 * error states are standardized here so the grid stays visually consistent
 * while sections resolve independently.
 */
export const SectionCard = React.forwardRef(function SectionCard(
  {
    title,
    subtitle,
    icon,
    accent = "#4f46e5",
    loading = false,
    error = null,
    partial = false,
    onRetry,
    headerRight = null,
    link = null,
    size = "md",
    children,
  },
  ref,
) {
  return (
    <section
      ref={ref}
      className={`oo-card oo-card--${size}`}
      style={{ "--oo-accent": accent }}
    >
      <div className="oo-card-head">
        <span className="oo-card-icon" style={{ color: accent, background: `${accent}18` }}>
          {icon}
        </span>
        <div className="oo-card-title-wrap">
          <h3 className="oo-card-title">{title}</h3>
          {subtitle ? <p className="oo-card-sub">{subtitle}</p> : null}
        </div>
        <div className="oo-card-head-right">
          {partial ? (
            <span className="oo-chip oo-chip--warn" title="Some data was truncated to keep this fast">
              Partial
            </span>
          ) : null}
          {headerRight}
        </div>
      </div>

      <div className="oo-card-body">
        {error ? (
          <div className="oo-card-state oo-card-state--error">
            <FaExclamationTriangle />
            <div className="oo-card-state-text">{error}</div>
            {onRetry ? (
              <button type="button" className="oo-mini-btn" onClick={onRetry}>
                <FaRedo /> Retry
              </button>
            ) : null}
          </div>
        ) : loading ? (
          <div className="oo-skel-stack">
            <div className="ou-skel ou-skel-line" style={{ width: "60%" }} />
            <div className="ou-skel" style={{ height: 64, marginTop: 10 }} />
            <div className="ou-skel ou-skel-line" style={{ width: "40%", marginTop: 12 }} />
          </div>
        ) : (
          children
        )}
      </div>

      {link ? (
        <div className="oo-card-foot">
          {link.external ? (
            <a
              className="oo-card-link"
              href={link.to}
              target="_blank"
              rel="noopener noreferrer"
            >
              {link.label}
              <FaExternalLinkAlt style={{ fontSize: 10 }} />
            </a>
          ) : (
            <Link
              className="oo-card-link"
              to={link.state ? { pathname: link.to, state: link.state } : link.to}
            >
              {link.label}
              <FaArrowRight style={{ fontSize: 11 }} />
            </Link>
          )}
        </div>
      ) : null}
    </section>
  );
});

/** Small inline stat used inside section bodies. */
export function MiniStat({ label, value, accent }) {
  return (
    <div className="oo-ministat">
      <div className="oo-ministat-value" style={accent ? { color: accent } : undefined}>
        {value}
      </div>
      <div className="oo-ministat-label">{label}</div>
    </div>
  );
}
